import React, { useEffect, useState, useMemo } from "react";
import {
  ExclamationCircleIcon,
  InboxIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  SunIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";
import MonthYearPicker from "./MonthYearPicker";

const LATE_CHECKIN_TIME = "10:40:00";
const TABLE_HEIGHT = "max-h-[460px]";

const formatTime = (t) => {
  if (!t) return "—";
  const [hours, minutes] = t.split(":");
  const h = parseInt(hours, 10);
  return `${h % 12 || 12}:${minutes} ${h >= 12 ? "PM" : "AM"}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const getDayName = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short" });
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "—";
  const base = new Date().toISOString().split("T")[0];
  const diff = new Date(`${base}T${end}`) - new Date(`${base}T${start}`);
  if (diff <= 0) return "—";
  return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`;
};

const getLateInfo = (checkIn) => {
  if (!checkIn) return null;
  const base = new Date(`1970-01-01T${LATE_CHECKIN_TIME}`);
  const cin  = new Date(`1970-01-01T${checkIn}`);
  if (cin <= base) return null;
  const diff = cin - base;
  return `+${String(Math.floor(diff / 3600000)).padStart(2, "0")}:${String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0")} late`;
};

/* ── Status config ─────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  Present:     { icon: CheckCircleIcon,  rowBg: "",                     badge: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-500" },
  Absent:      { icon: XCircleIcon,      rowBg: "bg-rose-50/60",        badge: "bg-rose-50 text-rose-700 border border-rose-200",          dot: "bg-rose-500" },
  Leave:       { icon: CalendarDaysIcon, rowBg: "bg-amber-50/60",       badge: "bg-amber-50 text-amber-700 border border-amber-200",       dot: "bg-amber-500" },
  "Half Day":  { icon: MinusCircleIcon,  rowBg: "bg-blue-50/40",        badge: "bg-blue-50 text-blue-700 border border-blue-200",          dot: "bg-blue-500" },
  Holiday:     { icon: SunIcon,          rowBg: "bg-sky-50/40",         badge: "bg-sky-50 text-sky-700 border border-sky-200",             dot: "bg-sky-500" },
  "Week Off":  { icon: MinusCircleIcon,  rowBg: "",                     badge: "bg-slate-100 text-slate-600 border border-slate-200",      dot: "bg-slate-400" },
  "No Record": { icon: MinusCircleIcon,  rowBg: "",                     badge: "bg-slate-50 text-slate-400 border border-slate-100",       dot: "bg-slate-300" },
};

const getCfg = (s) => STATUS_CONFIG[s] ?? STATUS_CONFIG["No Record"];

/* ── Summary Bar ─────────────────────────────────────────────────────────── */
const SummaryBar = ({ data }) => {
  const counts = { Present: 0, Absent: 0, Leave: 0, "Half Day": 0, Holiday: 0, "Week Off": 0 };
  data.forEach((r) => { if (counts[r.status] !== undefined) counts[r.status]++; });

  const colorMap = {
    Present:    "bg-emerald-50 text-emerald-700 border border-emerald-200",
    Absent:     "bg-rose-50 text-rose-700 border border-rose-200",
    Leave:      "bg-amber-50 text-amber-700 border border-amber-200",
    "Half Day": "bg-blue-50 text-blue-700 border border-blue-200",
    Holiday:    "bg-sky-50 text-sky-700 border border-sky-200",
    "Week Off": "bg-slate-100 text-slate-600 border border-slate-200",
  };

  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(counts).map(([key, val]) => (
        <div
          key={key}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorMap[key]}`}
        >
          <span className={`w-2 h-2 rounded-full ${getCfg(key).dot}`} />
          {key} <span className="font-extrabold ml-0.5">{val}</span>
        </div>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const AttendanceTable = ({ data = [], isLoading = false, error = null, fetchAttendance }) => {
  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selYear, setSelYear]   = useState(now.getFullYear());

  useEffect(() => {
    const handler = () => { if (typeof fetchAttendance === "function") fetchAttendance(); };
    window.addEventListener("attendance-updated", handler);
    return () => window.removeEventListener("attendance-updated", handler);
  }, [fetchAttendance]);

  const filteredData = useMemo(() => {
    if (!selMonth || !selYear) return data;
    return data.filter((r) => {
      if (!r.attendance_date) return false;
      const d = new Date(r.attendance_date + "T00:00:00");
      return d.getMonth() + 1 === selMonth && d.getFullYear() === selYear;
    });
  }, [data, selMonth, selYear]);

  /* ── Loading ── */
  if (isLoading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <div className="w-7 h-7 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm font-medium text-slate-500">Loading attendance records…</span>
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div className="flex flex-col items-center justify-center py-16 gap-2">
      <ExclamationCircleIcon className="w-8 h-8 text-rose-500" />
      <span className="text-sm font-medium text-rose-600">{error}</span>
    </div>
  );

  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="flex flex-col gap-4">

        {/* Controls row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <MonthYearPicker
            selectedMonth={selMonth}
            selectedYear={selYear}
            onChange={({ month, year }) => {
              setSelMonth(month ?? now.getMonth() + 1);
              setSelYear(year  ?? now.getFullYear());
            }}
            label="Select Month"
          />
          <SummaryBar data={filteredData} />
        </div>

        {/* Empty state */}
        {filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-2 rounded-xl border border-slate-200 bg-slate-50">
            <InboxIcon className="w-9 h-9 text-slate-400" />
            <span className="text-sm font-medium text-slate-500">No records for this month.</span>
          </div>
        ) : (
          <div
            className="rounded-xl overflow-hidden border border-slate-200"
            style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
          >
            <div className={`overflow-y-auto overflow-x-auto att-scrollbar ${TABLE_HEIGHT}`}>
              <table className="w-full text-sm text-left">

                {/* thead — same #f8fafc sticky header as AdminAttendanceOverview */}
                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                  <tr>
                    {["Date", "Check In", "Check-In Location", "Check Out", "Check-Out Location", "Hours", "Status", "Remarks"].map((h) => (
                      <th key={h} className="py-3 px-4 whitespace-nowrap border-b border-slate-200">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((record, idx) => {
                    const cfg      = getCfg(record.status);
                    const Icon     = cfg.icon;
                    const lateInfo = getLateInfo(record.check_in);
                    let remarks    = null;

                    if (record.holiday_name) {
                      remarks = <span className="text-sky-700 font-medium text-xs">🎉 {record.holiday_name}</span>;
                    } else if (record.leave_type) {
                      remarks = (
                        <span className="text-amber-700 font-medium text-xs">
                          📋 {record.leave_type}
                          {record.leave_status === "pending" && (
                            <span className="ml-1 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                              Pending
                            </span>
                          )}
                        </span>
                      );
                    } else if (lateInfo && record.status === "Present") {
                      remarks = <span className="text-amber-600 font-mono text-xs">⏰ {lateInfo}</span>;
                    }

                    return (
                      <tr
                        key={record.attendance_id ?? `${record.attendance_date}-${idx}`}
                        className={`border-t border-slate-100 transition-colors hover:bg-blue-50/60 ${cfg.rowBg}`}
                      >
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 text-xs">{formatDate(record.attendance_date)}</span>
                            <span className="text-[10px] text-slate-400">{record.day_of_week ?? getDayName(record.attendance_date)}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {record.check_in
                            ? <span className="font-mono text-xs text-slate-700">{formatTime(record.check_in)}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-4 max-w-[140px]">
                          <span className="truncate block text-xs text-slate-500">{record.check_in_location_name || "—"}</span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {record.check_out
                            ? <span className="font-mono text-xs text-slate-700">{formatTime(record.check_out)}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-4 max-w-[140px]">
                          <span className="truncate block text-xs text-slate-500">{record.check_out_location_name || "—"}</span>
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {record.work_hours
                            ? <span className="font-mono text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md">{parseFloat(record.work_hours).toFixed(1)}h</span>
                            : record.check_in && record.check_out
                              ? <span className="font-mono text-xs text-slate-600">{calculateDuration(record.check_in, record.check_out)}</span>
                              : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
                            <Icon className="w-3.5 h-3.5 shrink-0" />
                            {record.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs whitespace-nowrap">
                          {remarks ?? <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

              </table>
            </div>

            {/* Footer */}
            <div className="px-5 py-2 border-t border-slate-100 flex justify-between bg-slate-50">
              <span className="text-xs text-slate-400">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
              <span className="text-xs text-slate-400">
                {new Date(selYear, selMonth - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AttendanceTable;